import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getBills, getPriorities } from "@/lib/api";
import { positionBadge, StageStepper } from "@/lib/bills";
import { Search } from "lucide-react";
import { InlineAssistant } from "@/components/AssistantWidget";
import { useT } from "@/lib/t";
import { useLang } from "@/context/LangContext";

const POSITIONS = ["All", "Support", "Oppose", "Monitor", "Amend"];
const STAGES = ["All", "Introduced", "Committee", "Passed House", "Passed Senate", "Signed", "Vetoed"];

export default function LegislativeWork() {
  const t = useT();
  const { lang } = useLang();
  const [bills, setBills] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [position, setPosition] = useState("All");
  const [stage, setStage] = useState("All");
  const [issue, setIssue] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => { getPriorities(lang).then(setPriorities); }, [lang]);

  useEffect(() => {
    const params = {};
    if (position !== "All") params.position = position;
    if (stage !== "All") params.stage = stage;
    if (issue !== "All") params.issue = issue;
    if (q.trim()) params.q = q.trim();
    getBills(params).then(setBills);
  }, [position, stage, issue, q]);

  return (
    <div data-testid="legislative-page">
      <section className="bg-ccc-plum text-ccc-paper py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="eyebrow eyebrow-light mb-3">{t("bills.eyebrow")}</div>
          <h1 className="font-serif text-5xl mb-4">{t("bills.hero_title")}</h1>
          <p className="text-ccc-paper/85 max-w-2xl" style={{ fontSize: "18px", lineHeight: 1.7 }}>
            {t("bills.hero_body")}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-4 gap-3 mb-8" data-testid="bill-filters">
          <div className="relative md:col-span-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ccc-muted"/>
            <input placeholder={t("bills.search_placeholder")}
              data-testid="bill-search"
              value={q} onChange={e => setQ(e.target.value)}
              className="w-full pl-9 pr-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum bg-white"/>
          </div>
          <select data-testid="filter-position" value={position} onChange={e => setPosition(e.target.value)}
            aria-label={t("bills.filter_position")}
            className="px-3 py-3 border border-ccc-rule bg-white focus:outline-none focus:border-ccc-plum">
            {POSITIONS.map(p => <option key={p}>{p}</option>)}
          </select>
          <select data-testid="filter-stage" value={stage} onChange={e => setStage(e.target.value)}
            aria-label={t("bills.filter_stage")}
            className="px-3 py-3 border border-ccc-rule bg-white focus:outline-none focus:border-ccc-plum">
            {STAGES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2 mb-8" data-testid="issue-chips">
          <button onClick={() => setIssue("All")} data-testid="issue-chip-all"
            className={`px-3 py-1 uppercase tracking-wider border transition-colors ${issue === "All" ? "bg-ccc-plum text-white border-ccc-plum" : "border-ccc-rule text-ccc-body hover:border-ccc-plum"}`}
            style={{ fontSize: "13px" }}>
            {t("bills.filter_all_issues")}
          </button>
          {priorities.map(p => (
            <button key={p.slug} onClick={() => setIssue(p.slug)}
              className={`px-3 py-1 uppercase tracking-wider border transition-colors ${issue === p.slug ? "bg-ccc-plum text-white border-ccc-plum" : "border-ccc-rule text-ccc-body hover:border-ccc-plum"}`}
              style={{ fontSize: "13px" }}>
              {p.title}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6" data-testid="bill-list">
          {bills.length === 0 && (
            <div className="col-span-full text-center text-ccc-muted py-16 font-serif text-xl">
              {t("bills.empty")}
            </div>
          )}
          {bills.map(b => (
            <Link key={b.id} to={`/legislative-work/${b.id}`} data-testid={`bill-${b.number}`}
              className="bg-white border border-ccc-rule p-6 hover:border-ccc-plum hover:shadow-sm transition-all group">
              <div className="flex items-start justify-between mb-3 gap-3">
                <div>
                  <span className="font-mono text-xs text-ccc-muted">{b.number} &middot; {b.session}</span>
                  <h3 className="font-serif text-2xl text-ccc-plum group-hover:text-ccc-plum-deep transition-colors mt-1 leading-tight">{b.title}</h3>
                </div>
                <span className={`px-2 py-1 uppercase tracking-wider font-semibold shrink-0 ${positionBadge(b.position)}`} style={{ fontSize: "11px" }}>
                  {b.position}
                </span>
              </div>
              <StageStepper stage={b.stage}/>
              <p className="text-ccc-body mt-4 line-clamp-2" style={{ fontSize: "16px" }}>{b.summary}</p>
              {b.hearing_date && (
                <div className="text-ccc-muted mt-3" style={{ fontSize: "14px" }}>
                  {t("bills.next_hearing")}: {b.hearing_date} &middot; {b.committee}
                </div>
              )}
            </Link>
          ))}
        </div>

        <div className="mt-16 max-w-3xl">
          <InlineAssistant tone="bill" />
        </div>
      </section>
    </div>
  );
}
